import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import {
    login as authLogin,
    verifyOtp as authVerifyOtp,
    logout as authLogout,
    isAuthenticated as checkIsAuthenticated,
    getCurrentUser as getStoredUser,
    getAuthToken as getStoredToken,
    refreshAccessToken as refreshTokenApi,
    isTokenExpired,
    isTokenExpiringSoon,
    isRefreshTokenValid,
    getRefreshTokenExpiryTime,
    resetRefreshState as resetAuthModuleRefreshState,
    initializeTokenValidation,
} from '../services/auth';
import { getAccountInfo, resetFailedRefreshState } from '../services/api';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(() => getStoredUser());
    const [accessToken, setAccessToken] = useState(() => getStoredToken());
    const [isAuth, setIsAuth] = useState(() => checkIsAuthenticated());
    const [loading, setLoading] = useState(false);
    const [initializing, setInitializing] = useState(true);

    // Add refs to prevent race conditions
    const isRefreshing = useRef(false);
    const refreshTimeout = useRef(null);
    const proactiveRefreshTimeout = useRef(null);
    const refreshExpiryTimeout = useRef(null);
    const lastRefreshTime = useRef(0);
    const hasInitialized = useRef(false);

    // Helper to update state from storage
    const syncFromStorage = useCallback(() => {
        setUser(getStoredUser());
        setAccessToken(getStoredToken());
        setIsAuth(checkIsAuthenticated());
    }, []);



    // Debounced refresh function to prevent rapid calls
    const debouncedRefresh = useCallback(async () => {
        if (isRefreshing.current) {
            return;
        }

        // Prevent refreshing too frequently (minimum 10 seconds between attempts)
        const now = Date.now();
        if (now - lastRefreshTime.current < 10000) {
            return;
        }

        // Clear any existing timeout
        if (refreshTimeout.current) {
            clearTimeout(refreshTimeout.current);
        }

        // Debounce refresh calls with shorter delay
        refreshTimeout.current = setTimeout(async () => {
            if (isRefreshing.current) return;

            isRefreshing.current = true;
            lastRefreshTime.current = Date.now();

            try {
                console.log("Attempting token refresh...");
                await refreshTokenApi();
                console.log("Token refreshed successfully");
                syncFromStorage();
            } catch (error) {
                console.error("Failed to refresh token:", error);
                // Only logout if refresh token is clearly invalid/missing
                if (
                    error.message.includes("No refresh token") ||
                    error.message.includes("Refresh token is invalid") ||
                    error.message.includes("invalid or expired")
                ) {
                    authLogout();
                    setUser(null);
                    setAccessToken(null);
                    setIsAuth(false);
                }
            } finally {
                isRefreshing.current = false;
            }
        }, 1000); // 1 second debounce (reduced from 2 seconds)
    }, [syncFromStorage]);

    // No-op scheduler; proactive refresh will run on a fixed interval instead
    const scheduleProactiveRefresh = useCallback(() => {
        return;
    }, []);

    // Enhanced initialization auth state on app start
    useEffect(() => {
        const initializeAuth = async () => {
            // Prevent multiple initializations
            if (hasInitialized.current) return;
            hasInitialized.current = true;

            try {
                console.log("Initializing authentication...");

                const storedAccessToken = getStoredToken();
                if (!isRefreshTokenValid() && !storedAccessToken) {
                    authLogout();
                    setUser(null);
                    setAccessToken(null);
                    setIsAuth(false);
                    console.log("Refresh token invalid at init - logging out");
                    return setInitializing(false);
                }

                // Use the new initialization function from auth service
                const result = await initializeTokenValidation();

                if (result.success) {
                    console.log("Authentication initialized successfully:", result.reason);
                    // Sync state from storage after successful initialization
                    syncFromStorage();
                } else {
                    console.log("Authentication initialization failed:", result.reason);
                    const shouldForceLogout = (
                        result?.reason === 'no_refresh_token' ||
                        result?.reason === 'refresh_token_expired' ||
                        result?.reason === 'refresh_failed_invalid_token'
                    );
                    if (shouldForceLogout) {
                        authLogout();
                        setUser(null);
                        setAccessToken(null);
                        setIsAuth(false);
                        console.log("Auth state cleared due to invalid refresh");
                    } else {
                        console.log("Non-fatal init failure; remaining on login if unauthenticated");
                    }
                }

            } catch (error) {
                console.error("Error initializing auth:", error);
                // On any unexpected error, clear auth state and redirect to login
                authLogout();
                setUser(null);
                setAccessToken(null);
                setIsAuth(false);
            } finally {
                setInitializing(false);
            }
        };

        initializeAuth();
    }, [syncFromStorage]);

    // Login method (Step 1: credentials -> sessionToken)
    const login = async (credentials) => {
        setLoading(true);
        try {
            const data = await authLogin(credentials);
            return data;
        } finally {
            setLoading(false);
        }
    };

    // Verify OTP method (Step 2: sessionToken + otpCode -> tokens)
    const verifyOtp = async ({ sessionToken, otpCode }) => {
        setLoading(true);
        try {
            const data = await authVerifyOtp({ sessionToken, otpCode });
            // Reset failed refresh state after successful verification
            resetFailedRefreshState();
            resetAuthModuleRefreshState();
            // Fetch full account info after login
            try {
                const account = await getAccountInfo();
                localStorage.setItem('user', JSON.stringify(account));
            } catch (e) {
                console.warn("Failed to fetch account info after OTP verification:", e);
            }
            // Update state immediately after successful verification
            setUser(getStoredUser());
            setAccessToken(getStoredToken());
            setIsAuth(true);
            return data;
        } finally {
            setLoading(false);
        }
    };

    // Logout method
    const logout = () => {
        // Clear any pending refresh operations
        if (refreshTimeout.current) {
            clearTimeout(refreshTimeout.current);
        }
        if (proactiveRefreshTimeout.current) {
            clearTimeout(proactiveRefreshTimeout.current);
        }
        if (refreshExpiryTimeout.current) {
            clearTimeout(refreshExpiryTimeout.current);
        }
        isRefreshing.current = false;

        authLogout();
        resetFailedRefreshState();
        resetAuthModuleRefreshState();
        // Update state immediately after logout
        setUser(null);
        setAccessToken(null);
        setIsAuth(false);
    };

    // Refresh token method
    const refreshToken = async () => {
        setLoading(true);
        try {
            const newToken = await refreshTokenApi();
            syncFromStorage();
            return newToken;
        } finally {
            setLoading(false);
        }
    };

    // Listen for storage changes (multi-tab support)
    useEffect(() => {
        const handleStorage = (e) => {
            if (["accessToken", "refreshToken", "user"].includes(e.key)) {
                syncFromStorage();
            }
        };
        window.addEventListener('storage', handleStorage);
        return () => window.removeEventListener('storage', handleStorage);
    }, [syncFromStorage]);

    // Listen for in-tab token refresh and logout events to keep context in sync
    useEffect(() => {
        const onTokenRefreshed = () => {
            syncFromStorage();
        };
        const onLoggedOut = () => {
            // Clear state immediately on logout so routes redirect to login
            if (proactiveRefreshTimeout.current) {
                clearTimeout(proactiveRefreshTimeout.current);
            }
            if (refreshExpiryTimeout.current) {
                clearTimeout(refreshExpiryTimeout.current);
            }
            setUser(null);
            setAccessToken(null);
            setIsAuth(false);
        };
        window.addEventListener('auth:tokenRefreshed', onTokenRefreshed);
        window.addEventListener('auth:loggedOut', onLoggedOut);
        return () => {
            window.removeEventListener('auth:tokenRefreshed', onTokenRefreshed);
            window.removeEventListener('auth:loggedOut', onLoggedOut);
        };
    }, [syncFromStorage, scheduleProactiveRefresh]);

    useEffect(() => {
        if (refreshExpiryTimeout.current) {
            clearTimeout(refreshExpiryTimeout.current);
        }
        if (!isAuth) return;

        const expiryTime = getRefreshTokenExpiryTime();
        if (!expiryTime) return;

        const delay = Math.max(0, expiryTime - Date.now());
        refreshExpiryTimeout.current = setTimeout(() => {
            authLogout();
            resetFailedRefreshState();
            resetAuthModuleRefreshState();
            setUser(null);
            setAccessToken(null);
            setIsAuth(false);
        }, delay);

        return () => {
            if (refreshExpiryTimeout.current) {
                clearTimeout(refreshExpiryTimeout.current);
            }
        };
    }, [isAuth, accessToken]);

    // Listen for visibility changes to refresh token when user returns to tab
    useEffect(() => {
        const handleVisibilityChange = async () => {
            if (!document.hidden && isAuth) {
                const now = Date.now();
                // Only attempt validation if enough time has passed since last refresh
                if (now - lastRefreshTime.current > 30000) { // At least 30 seconds between visibility checks
                    const result = await initializeTokenValidation();
                    if (result.success) {
                        syncFromStorage();
                    } else {
                        const shouldForceLogout = (
                            result?.reason === 'no_refresh_token' ||
                            result?.reason === 'refresh_token_expired' ||
                            result?.reason === 'refresh_failed_invalid_token'
                        );
                        if (shouldForceLogout) {
                            authLogout();
                            setUser(null);
                            setAccessToken(null);
                            setIsAuth(false);
                        }
                    }
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [isAuth, syncFromStorage]);

    // Proactive token refresh every 5 minutes
    useEffect(() => {
        if (!isAuth) return;

        const interval = setInterval(async () => {
            const result = await initializeTokenValidation();
            if (result.success) {
                syncFromStorage();
            } else {
                const shouldForceLogout = (
                    result?.reason === 'no_refresh_token' ||
                    result?.reason === 'refresh_token_expired' ||
                    result?.reason === 'refresh_failed_invalid_token'
                );
                if (shouldForceLogout) {
                    authLogout();
                    setUser(null);
                    setAccessToken(null);
                    setIsAuth(false);
                }
            }
        }, 5 * 60 * 1000); // Check every 5 minutes

        return () => clearInterval(interval);
    }, [isAuth, syncFromStorage]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (refreshTimeout.current) {
                clearTimeout(refreshTimeout.current);
            }
            if (proactiveRefreshTimeout.current) {
                clearTimeout(proactiveRefreshTimeout.current);
            }
            if (refreshExpiryTimeout.current) {
                clearTimeout(refreshExpiryTimeout.current);
            }
        };
    }, []);

    // Expose context value
    const value = {
        user,
        accessToken,
        isAuth,
        loading,
        initializing,
        login,
        verifyOtp,
        logout,
        refreshToken,
        syncFromStorage,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext); 
